import AntIcon from "@/components/icons/AntIcon";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import DisplayWrapper from "@/components/wrappers/DisplayWrapper";
import { generateRandomToken, openUrl } from "@/utils/utils";
import { Button, Card } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetPartnerConfigsAndSsoProperties,
  useGetPartnerSso,
  useLogoutFromPartnerSso,
} from "../../../common/api/queries";
import { Permission } from "../../../common/security/enums";
import ComponentWithPermission from "../../auth/components/ComponentWithPermission";
import { PartnerConfigForm } from "../components/forms/PartnerConfigForm";
import PartnerSsoUsernameForm from "../components/forms/PartnerSsoUsernameForm";
import { PartnerConfigListView } from "../components/views/PartnerConfigListView";
import { PartnerSsoType } from "../enums";
import { usePartnerSsoActions } from "../store";
import { PartnerConfig } from "../types";
import { redirectToMonlyAccessTokenLogin, redirectToMonlyAuthorizationCodeLogin } from "../utils";

export const PartnerConfigsContainer = () => {
  const { t } = useTranslation();

  const [configFormOpen, setConfigFormOpen] = useState<boolean>(false);
  const [configsToUpdate, setConfigsToUpdate] = useState<PartnerConfig[]>();
  const [ssoType, setSsoType] = useState<PartnerSsoType>();

  const partnerSsoActions = usePartnerSsoActions();

  const getPartnerSso = useGetPartnerSso();
  const logoutFromPartnerSso = useLogoutFromPartnerSso();
  const { data: configsAndSsoProps, isError } = useGetPartnerConfigsAndSsoProperties();

  const handleLogoutClick = (configs: PartnerConfig[]) => {
    const ssoTypesToLogout = configs
      .filter((config) => config.ssoType)
      .map((config) => config.ssoType)
      .filter((ssoType) => !!ssoType);

    if (ssoTypesToLogout.length) {
      logoutFromPartnerSso.mutate({ ssoTypes: ssoTypesToLogout });
    }
  };

  const handleUpdateClick = (configs: PartnerConfig[]) => {
    setConfigFormOpen(true);
    setConfigsToUpdate(configs);
  };

  const handleFormCancel = () => {
    setConfigFormOpen(false);
    setConfigsToUpdate(undefined);
  };

  const handleGetPartnerSso = (ssoType: PartnerSsoType, username?: string) => {
    getPartnerSso.mutate(
      { ssoType, username },
      {
        onSuccess: (responseData) => {
          switch (ssoType) {
            case PartnerSsoType.MONLY:
              if (responseData) {
                redirectToMonlyAccessTokenLogin(
                  responseData.accessToken ?? "",
                  "_blank",
                  configsAndSsoProps?.monlySsoProperties?.baseUrl ?? "",
                );
              } else {
                const token = "MONLY_" + generateRandomToken(20);
                partnerSsoActions.ssoLoginStarted(token);
                redirectToMonlyAuthorizationCodeLogin(token, configsAndSsoProps?.monlySsoProperties);
              }
              break;
            case PartnerSsoType.METLIFE:
              if (responseData?.redirectUrl) {
                openUrl(responseData.redirectUrl, "_blank");
              } else {
                setSsoType(PartnerSsoType.METLIFE);
              }
              break;
          }
        },
      },
    );
  };

  const handleSsoUsernameFormSubmit = (ssoType: PartnerSsoType, username: string) => {
    setSsoType(undefined);
    handleGetPartnerSso(ssoType, username);
  };

  return (
    <>
      <ContentWrapper>
        <DisplayWrapper itemLoaded={!!configsAndSsoProps} isError={isError}>
          {configsAndSsoProps && (
            <Card
              className="card-box"
              title={<h2>{t("partnerConfig.titles.list")}</h2>}
              extra={
                <ComponentWithPermission permissions={[Permission.ADMIN_PARTNER_CONFIGS]}>
                  <Button type="primary" icon={<AntIcon type="plus" />} onClick={() => setConfigFormOpen(true)}>
                    {t("common.add")}
                  </Button>
                </ComponentWithPermission>
              }
            >
              <PartnerConfigListView
                configList={configsAndSsoProps.configs}
                onPartnerSsoGet={handleGetPartnerSso}
                onLogoutClick={handleLogoutClick}
                onUpdateClick={handleUpdateClick}
              />
            </Card>
          )}
        </DisplayWrapper>

        <PartnerConfigForm
          configsAndSsoProps={configsAndSsoProps}
          open={configFormOpen}
          configs={configsToUpdate}
          onFormCancel={handleFormCancel}
        />
      </ContentWrapper>

      <PartnerSsoUsernameForm
        open={!!ssoType}
        ssoType={ssoType}
        onFormSubmit={handleSsoUsernameFormSubmit}
        onFormCancel={() => setSsoType(undefined)}
      />
    </>
  );
};
